import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  Building2, ChevronLeft, QrCode, Mail, CreditCard, Users,
  Edit2, Save, X, Download, Printer, ExternalLink, Copy, Check
} from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Retailer Detail Page
 * 
 * Shows: Profile, Contact, QR Codes, Staff, Revenue, Passes Sold, Payment History
 */
export default function KyleRiseRetailerDetail() {
  const navigate = useNavigate();
  const { retailerId } = useParams();
  
  const [retailer, setRetailer] = useState(null);
  const [staffPasses, setStaffPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [copied, setCopied] = useState(false);
  const [qrData, setQrData] = useState(null);

  useEffect(() => {
    fetchRetailer();
    fetchStaffPasses();
    fetchQRData();
  }, [retailerId]);

  const fetchRetailer = async () => {
    try {
      const res = await axios.get(`${API}/kyle-rise/retailers/${retailerId}`);
      setRetailer(res.data);
      setEditData({
        name: res.data.name,
        contact_email: res.data.contact_email,
        staff_pass_price_pence: res.data.staff_pass_price_pence,
        active: res.data.active
      });
    } catch (err) {
      console.error("Failed to fetch retailer:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffPasses = async () => {
    try {
      const res = await axios.get(`${API}/kyle-rise/passes?pass_type=staff&retailer_id=${retailerId}&limit=10`);
      setStaffPasses(res.data.passes || []);
    } catch (err) {
      console.error("Failed to fetch staff passes:", err);
    }
  };

  const fetchQRData = async () => {
    try {
      const res = await axios.get(`${API}/kyle-rise/retailers/${retailerId}/qr`);
      setQrData(res.data);
    } catch (err) {
      console.error("Failed to fetch QR data:", err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/kyle-rise/retailers/${retailerId}`, editData);
      await fetchRetailer();
      setEditing(false);
    } catch (err) {
      console.error("Failed to update retailer:", err);
    }
  };

  const copyUrl = () => {
    if (qrData?.staff_portal_url) {
      navigator.clipboard.writeText(qrData.staff_portal_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!retailer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Retailer not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kyle-rise-retailer-detail">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate("/admin/kyle-rise/retailers")}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{retailer.name}</h1>
                <p className="text-sm text-gray-500">{retailer.retailer_id}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button
                  onClick={() => setEditing(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Profile</h3>
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Name</label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={editData.contact_email}
                    onChange={(e) => setEditData({ ...editData, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Staff Pass Price (pence)</label>
                  <input
                    type="number"
                    value={editData.staff_pass_price_pence}
                    onChange={(e) => setEditData({ ...editData, staff_pass_price_pence: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editData.active}
                    onChange={(e) => setEditData({ ...editData, active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label className="text-sm text-gray-700">Active</label>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Contact Email</p>
                  <p className="text-sm font-medium">{retailer.contact_email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Staff Pass Price</p>
                  <p className="text-sm font-medium">{retailer.staff_pass_price_display}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    retailer.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                  }`}>
                    {retailer.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="text-sm">{new Date(retailer.created_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>

          {/* Stats Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Statistics</h3>
            <div className="space-y-4">
              <div 
                onClick={() => navigate(`/admin/kyle-rise/staff-passes?retailer_id=${retailerId}`)}
                className="flex items-center justify-between p-3 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-600" />
                  <span className="text-sm text-orange-800">Staff Passes</span>
                </div>
                <span className="font-bold text-orange-700">{retailer.staff_pass_count || 0}</span>
              </div>
              <div 
                onClick={() => navigate(`/admin/kyle-rise/staff-passes?retailer_id=${retailerId}&status=active`)}
                className="flex items-center justify-between p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-800">Active Passes</span>
                </div>
                <span className="font-bold text-green-700">{retailer.active_pass_count || 0}</span>
              </div>
              <div 
                onClick={() => navigate(`/admin/kyle-rise/staff-passes?retailer_id=${retailerId}&status=pending`)}
                className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm text-yellow-800">Pending Passes</span>
                </div>
                <span className="font-bold text-yellow-700">{retailer.pending_pass_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-teal-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-teal-600" />
                  <span className="text-sm text-teal-800">Total Revenue</span>
                </div>
                <span className="font-bold text-teal-700">{retailer.total_revenue_display || "£0.00"}</span>
              </div>
            </div>
          </div>

          {/* QR Code Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Staff Portal QR Code</h3>
            <div className="space-y-4">
              <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <QrCode className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">QR Code Preview</p>
                  <p className="text-xs text-gray-400 mt-1">Generate in QR Manager</p>
                </div>
              </div>
              {qrData && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={qrData.staff_portal_url}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs"
                    />
                    <button
                      onClick={copyUrl}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/kyle-rise/qr-manager?retailer=${retailerId}`)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <QrCode className="w-4 h-4" />
                      Manage QR
                    </button>
                    <button
                      onClick={() => window.open(qrData.staff_portal_url, '_blank')}
                      className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Staff Passes */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Recent Staff Passes</h3>
            <button
              onClick={() => navigate(`/admin/kyle-rise/staff-passes?retailer_id=${retailerId}`)}
              className="text-sm text-blue-600 hover:underline"
            >
              View All
            </button>
          </div>
          {staffPasses.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No staff passes yet</p>
          ) : (
            <div className="space-y-2">
              {staffPasses.map((pass) => (
                <div
                  key={pass.pass_id}
                  onClick={() => navigate(`/admin/kyle-rise/staff-passes/${pass.pass_id}`)}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium">{pass.staff_name}</p>
                    <p className="text-xs text-gray-500">{pass.reg_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{pass.amount_display}</p>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      pass.status === "active" ? "bg-green-100 text-green-700" :
                      pass.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                      "bg-gray-100 text-gray-600"
                    }`}>
                      {pass.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
